import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import RemoveUserBtn from "../components/adminUser/removeUserBtn.comp";
import SolidLink from "../components/form/solidLink.comp";
import Layout from "../components/layouts/layout.comp";
import { ListSection, Tag } from "../components/reuseables/list.comp";
import { UserStatus, UserType } from "../components/user/user.comp";
import { useShopId } from "../core/login.core";
import shopCore from "../core/shop.core";
import shopUserCore from "../core/shopUser.core";
import { useTranslation } from "../core/translation.core";
import { ShopFull } from "../types/shop";
import { AdminUser, AdminUserStatus } from "../types/user";
import AddLink from "../components/form/addLink.comp";
import PencilIcon from "../icons/pencil.icon";
import KeyIcon from "../icons/key.icon";
import EnvelopeIcon from "../icons/envelope.icon";
import SolidBtn from "../components/form/solidBtn.comp";

export default function UsersPage() {
  const { t, msg } = useTranslation();
  const shopId = useShopId();

  const { data: shop } = useQuery({
    queryKey: ["shop", shopId],
    queryFn: shopCore.getShop,
  });

  return (
    <Layout title={t(msg.user.users)} titleBtn={<AddLink id="user-create" href="/user/create" cap="manageUser" />}>
      <ListSection>
        <Table shop={shop} />
      </ListSection>
    </Layout>
  );
}

function Table({ shop }: { shop?: ShopFull }) {
  const { t, msg } = useTranslation();

  if (!shop) return null;
  if (!shop.shopUsers) return null;

  return (
    <div id="user-table" className="pt-6 lg:pt-0">
      <div className="hidden lg:grid lg:grid-cols-[300px_100px_100px_200px] gap-6 px-6 py-3 text-sm font-medium">
        <div>{t(msg.setting.email)}</div>
        <div>{t(msg.user.status)}</div>
        <div>{t(msg.user.role)}</div>
        <div></div>
      </div>
      {shop.shopUsers.map((user) => (
        <Row key={user.adminUserId} user={user} />
      ))}
    </div>
  );
}

function Row({ user }: { user: AdminUser }) {
  const { t, msg } = useTranslation();

  return (
    <div
      data-value={user.email}
      className="user-row grid grid-cols-[1fr_1fr] lg:grid-cols-[300px_100px_100px_200px] gap-x-6 gap-y-3 px-6 py-6 border-t-[1px] text-sm"
    >
      <div className="col-span-2 lg:col-span-1">
        <Tag>{t(msg.setting.email)}</Tag>
        {user.email} ({user.name})
      </div>

      <div data-value={user.status}>
        <Tag>{t(msg.user.status)}</Tag>
        <UserStatus status={user.status} />
      </div>
      <div data-value={user.type}>
        <Tag>{t(msg.user.role)}</Tag>
        <UserType type={user.type} />
      </div>
      <div className="flex gap-4 col-span-4">
        <RemoveUserBtn user={user} />
        <SolidLink
          className="user-edit bg-her-lightFrenchBeige"
          href={`/user/edit/${user.adminUserId}`}
          text={t(msg.form.edit)}
          icon={<PencilIcon className="w-4 h-4" />}
        />
        <SolidLink
          cap="manageUser"
          className="user-change-password"
          href={`/user/updateOtherPassword/${user.adminUserId}`}
          text={t(msg.user.updatePassword)}
          icon={<KeyIcon className="w-4 h-4" />}
        />
        <ResendButton user={user} />
      </div>
    </div>
  );
}

type SendStatus = "idle" | "sending" | "sent" | "failed";

function ResendButton({ user }: { user: AdminUser }) {
  const { t, msg } = useTranslation();

  const [sendStatus, setSendStatus] = useState<"idle" | "sending" | "sent" | "failed">();

  if (user.status !== AdminUserStatus.pending) return null;

  async function resend() {
    setSendStatus("sending");
    try {
      await shopUserCore.resendInvitation(user.email);
    } catch (e) {
      console.error(e);
      setSendStatus("failed");
    }
    setSendStatus("sent");
  }

  return (
    <>
      <button
        data-role="resend"
        onClick={resend}
        className="bg-her-mysticMaroon text-white flex items-center gap-2 px-2 py-1 rounded-md"
      >
        <EnvelopeIcon className="w-4 h-4" />
        {t(msg.user.resend)}
      </button>
      <StatusLabel status={sendStatus} />
    </>
  );
}

function StatusLabel({ status }: { status?: SendStatus }) {
  const { t, msg } = useTranslation();

  switch (status) {
    case "idle":
      return null;
    case "failed":
      return <p className="text-red-400">{t(msg.user.resendStatusFailed)}</p>;
    case "sending":
      return <p className="text-green-400">{t(msg.user.resendStatusSending)}</p>;
    case "sent":
      return <p className="text-blue-400 sent">{t(msg.user.resendStatusSent)}</p>;
    default:
      return null;
  }
}
